import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  Indicador,
  TituloDeSecao,
  Vazio,
} from '../componentes/base';
import { data, mesPorExtenso, moeda, percentual, plural } from '../util/formato';

interface LinhaDoPainel {
  readonly id: string | null;
  readonly clinica: { id: string; nome: string };
  readonly mesReferencia: string;
  readonly vencimento: string;
  readonly totalRecuperado: number;
  readonly quantidadePagamentos: number;
  readonly percentual: number;
  readonly honorariosExito: number;
  readonly mensalidade: number;
  readonly reembolsoCustas: number;
  readonly totalDevido: number;
  readonly status: string;
  readonly fechado: boolean;
}

interface PainelDeHonorarios {
  readonly mesReferencia: string;
  readonly nomeDoMes: string;
  readonly previsao: LinhaDoPainel[];
  readonly totalPrevisto: number;
  readonly emAberto: { total: number; quantidade: number };
  readonly vencidos: { total: number; quantidade: number };
  readonly recebidoNoAno: number;
}

const SITUACAO: Record<string, { rotulo: string; tom: 'neutro' | 'atencao' | 'recebido' }> = {
  PREVISTO: { rotulo: 'Previsto', tom: 'neutro' },
  ABERTO: { rotulo: 'Aberto', tom: 'atencao' },
  FATURADO: { rotulo: 'Faturado', tom: 'atencao' },
  PAGO: { rotulo: 'Recebido', tom: 'recebido' },
};

export function PaginaFechamentos() {
  const clienteConsulta = useQueryClient();
  const [aviso, setAviso] = useState<{ texto: string; tom: 'recebido' | 'vencido' } | null>(null);

  const { data: painel, isLoading } = useQuery({
    queryKey: ['fechamentos', 'painel'],
    queryFn: () => api.buscar<PainelDeHonorarios>('/fechamentos/painel'),
  });

  function recarregar() {
    void clienteConsulta.invalidateQueries({ queryKey: ['fechamentos'] });
  }

  const gerar = useMutation({
    mutationFn: () =>
      api.criar<{ gerados: unknown[]; falhas: Array<{ clinica: string; motivo: string }> }>(
        '/fechamentos/gerar',
        {},
      ),
    onSuccess: (r) => {
      setAviso({
        texto:
          `${plural(r.gerados.length, 'fechamento gerado', 'fechamentos gerados')}.` +
          (r.falhas.length > 0
            ? ` Não foi possível gerar: ${r.falhas.map((f) => `${f.clinica} (${f.motivo})`).join('; ')}`
            : ''),
        tom: r.falhas.length > 0 ? 'vencido' : 'recebido',
      });
      recarregar();
    },
    onError: (e) =>
      setAviso({
        texto:
          e instanceof FalhaDaApi ? e.message : 'Não foi possível gerar os fechamentos.',
        tom: 'vencido',
      }),
  });

  const faturar = useMutation({
    mutationFn: (id: string) => api.criar(`/fechamentos/${id}/faturar`, {}),
    onSuccess: () => {
      setAviso({ texto: 'Fechamento marcado como faturado à clínica.', tom: 'recebido' });
      recarregar();
    },
    onError: (e) =>
      setAviso({ texto: e instanceof FalhaDaApi ? e.message : 'Falha.', tom: 'vencido' }),
  });

  const receber = useMutation({
    mutationFn: (id: string) => api.criar(`/fechamentos/${id}/receber`, {}),
    onSuccess: () => {
      setAviso({ texto: 'Repasse recebido. As custas cobertas foram reembolsadas.', tom: 'recebido' });
      recarregar();
    },
    onError: (e) =>
      setAviso({ texto: e instanceof FalhaDaApi ? e.message : 'Falha.', tom: 'vencido' }),
  });

  if (isLoading || !painel) return <Carregando />;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Honorários e fechamento"
        descricao={`Apuração de ${mesPorExtenso(painel.mesReferencia).toLowerCase()} — o que as clínicas devem ao escritório`}
        acoes={
          <Botao tipo="primario" onClick={() => gerar.mutate()} desabilitado={gerar.isPending}>
            {gerar.isPending ? 'Gerando…' : 'Gerar o fechamento do mês'}
          </Botao>
        }
      />

      {aviso && (
        <div className="mb-5">
          <Alerta tom={aviso.tom}>{aviso.texto}</Alerta>
        </div>
      )}

      {/* O modelo de negócio inverte a intuição de todo mundo: o escritório não
          repassa, ele COBRA. A tela diz isso, porque quem chega novo sempre erra. */}
      <div className="mb-5">
        <Alerta tom="acento">
          O devedor paga direto na conta da clínica. No dia do repasse, a{' '}
          <strong>clínica paga ao escritório</strong> a mensalidade, o percentual de êxito
          sobre o recuperado e as custas adiantadas.
        </Alerta>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador
          rotulo="Previsto neste mês"
          valor={moeda(painel.totalPrevisto)}
          nota={mesPorExtenso(painel.mesReferencia)}
          tom="acento"
        />
        <Indicador
          rotulo="Em aberto"
          valor={moeda(painel.emAberto.total)}
          nota={plural(painel.emAberto.quantidade, 'fechamento', 'fechamentos')}
        />
        <Indicador
          rotulo="Vencido"
          valor={moeda(painel.vencidos.total)}
          nota={plural(painel.vencidos.quantidade, 'repasse atrasado', 'repasses atrasados')}
          tom={painel.vencidos.quantidade > 0 ? 'vencido' : 'neutro'}
        />
        <Indicador
          rotulo="Recebido no ano"
          valor={moeda(painel.recebidoNoAno)}
          nota="honorários já creditados"
          tom="recebido"
        />
      </div>

      <Cartao semPadding>
        <div className="p-5 pb-0">
          <TituloDeSecao auxiliar={`vencimento em ${data(painel.previsao[0]?.vencimento ?? null)}`}>
            Apuração por clínica
          </TituloDeSecao>
        </div>

        {painel.previsao.length === 0 ? (
          <Vazio titulo="Nenhuma clínica ativa cadastrada." />
        ) : (
          <div className="rolagem-lateral">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                  <th className="px-5 py-2.5 font-medium">Clínica</th>
                  <th className="px-3 py-2.5 text-right font-medium">Recuperado</th>
                  <th className="px-3 py-2.5 text-right font-medium">Êxito</th>
                  <th className="px-3 py-2.5 text-right font-medium">Mensalidade</th>
                  <th className="px-3 py-2.5 text-right font-medium">Custas</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total devido</th>
                  <th className="px-3 py-2.5 font-medium">Situação</th>
                  <th className="px-5 py-2.5 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {painel.previsao.map((linha) => {
                  const situacao = SITUACAO[linha.status] ?? {
                    rotulo: linha.status,
                    tom: 'neutro' as const,
                  };
                  return (
                    <tr key={linha.clinica.id} className="border-b last:border-0">
                      <td className="px-5 py-3">
                        <span className="font-medium text-[color:var(--texto)]">
                          {linha.clinica.nome}
                        </span>
                        <p className="text-[0.6875rem] text-[color:var(--texto-tenue)]">
                          {plural(linha.quantidadePagamentos, 'pagamento apurado', 'pagamentos apurados')}
                        </p>
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap">
                        {moeda(linha.totalRecuperado)}
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap">
                        {moeda(linha.honorariosExito)}
                        <span className="ml-1 text-[0.6875rem] text-[color:var(--texto-tenue)]">
                          {percentual(linha.percentual)}
                        </span>
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap">
                        {moeda(linha.mensalidade)}
                      </td>
                      <td className="tabular px-3 py-3 text-right whitespace-nowrap text-[color:var(--texto-suave)]">
                        {linha.reembolsoCustas > 0 ? moeda(linha.reembolsoCustas) : '—'}
                      </td>
                      <td
                        className="tabular px-3 py-3 text-right font-semibold whitespace-nowrap"
                        style={{ color: 'var(--acento)' }}
                      >
                        {moeda(linha.totalDevido)}
                      </td>
                      <td className="px-3 py-3">
                        <Ficha tom={situacao.tom}>{situacao.rotulo}</Ficha>
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {linha.id && linha.status === 'ABERTO' && (
                          <Botao
                            onClick={() => faturar.mutate(linha.id!)}
                            className="px-2.5 py-1 text-xs"
                          >
                            Faturar
                          </Botao>
                        )}
                        {linha.id && linha.status === 'FATURADO' && (
                          <Botao
                            tipo="primario"
                            onClick={() => receber.mutate(linha.id!)}
                            className="px-2.5 py-1 text-xs"
                          >
                            Registrar recebimento
                          </Botao>
                        )}
                        {!linha.fechado && (
                          <span className="text-xs text-[color:var(--texto-tenue)]">
                            ainda não gerado
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>

      <p className="mt-4 text-xs text-[color:var(--texto-tenue)]">
        Entram na apuração os pagamentos baixados até o fim do mês que ainda não foram
        apurados em nenhum fechamento — inclusive os lançados com atraso, que são
        recolhidos aqui em vez de se perderem.
      </p>
    </>
  );
}
